import React, {useState, Dispatch, SetStateAction} from 'react';
import {useTranslation} from 'react-i18next';
import {ADMIN_PAGES_KEYS, NS} from '../../../../../../i18n/constants/keys';
import {JobTitle} from '../../../../../../utils/types/admin_types';
import {
    JobDescription,
    JobPostingTabData,
} from '../../../../../../utils/types/JobOpenings';
import {Assessment} from '../../../../../../utils/types/assessment_types';
import {useGetPreQualConfigQuery} from '../../../../../../utils/api_service/preQualApi';
import {useGetCCAConfigQuery} from '../../../../../../utils/api_service/ccaApi';
import {formatDate} from '../../../../../../utils/helpers/dateUtils';

// Import components
import JobPostSettings from '../JobPostSettings';
import VideoMessageDetails from '../VideoMessageDetails';
import PreQualifyingQuestions from '../PreQualifyingQuestions';
import CCATab from '../CCATab';

interface JobPostConfigProps {
    jobTitleList: JobTitle[],
    jobDescription: JobDescription,
    jobPostingData: JobPostingTabData,
    setJobPostingData: Dispatch<SetStateAction<JobPostingTabData>>,
    setActiveTab: (tab: string) => void,
    setJobPostId: Dispatch<SetStateAction<string>>,
    jobPostId?: string,
    orgPerksAndBenefits: string,
    orgCompanyDetails: string,
    hasCCALicense?: boolean,
    assessmentInfo: Assessment | undefined,
    setAssessmentInfo: Dispatch<SetStateAction<Assessment | undefined>>,
    setOrgPerksAndBenefits: (value: (((prevState: string) => string) | string)) => void,
    setOrgCompanyDetails: (value: (((prevState: string) => string) | string)) => void,
    orgNames: string[]
}

type StepType = 'settings' | 'video' | 'prequal' | 'cca';

export const JobPostConfig = ({
                                  jobTitleList,
                                  jobDescription,
                                  jobPostingData,
                                  setJobPostingData,
                                  setActiveTab,
                                  setJobPostId,
                                  jobPostId,
                                  orgPerksAndBenefits,
                                  orgCompanyDetails,
                                  hasCCALicense = false,
                                  assessmentInfo,
                                  setAssessmentInfo,
                                  setOrgPerksAndBenefits,
                                  setOrgCompanyDetails,
                                  orgNames
                              }: JobPostConfigProps) => {
    const {t} = useTranslation(NS.adminPages);
    const [activeStep, setActiveStep] = useState<StepType>('settings');

    // Query pre-qual and CCA status for summaries
    const {data: preQualConfig} = useGetPreQualConfigQuery(jobPostId || '', {skip: !jobPostId});
    const preQualQuestionsCount = preQualConfig?.questions?.length || 0;

    const {data: ccaConfig} = useGetCCAConfigQuery(jobPostId || '', {skip: !jobPostId});
    const ccaEnabled = ccaConfig?.enabled ?? false;
    const ccaQuestionsCount = ccaConfig?.questions?.length || 0;

    // Formatting date range safely
    const formatDateString = (dateValue: any): string => {
        if (!dateValue) return 'TBD';
        const formatted = formatDate(dateValue, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
        return formatted || 'TBD';
    };

    // Check step completions
    const isSettingsCompleted = !!(
        jobPostingData.jobTitle &&
        jobPostingData.location &&
        jobPostingData.department &&
        jobPostingData.workType
    );

    const isVideoCompleted = !!(
        jobPostingData.enableVideo && jobPostingData.videoFilename
    );

    const isPrequalCompleted = preQualQuestionsCount > 0;

    const isCcaCompleted = !!(ccaEnabled && ccaQuestionsCount > 0);

    // Custom setActiveTab proxy to manage inner steps navigation
    const makeStepSetActiveTab = (step: StepType) => {
        return (tab: string | ((prev: string) => string)) => {
            const tabName = typeof tab === 'function' ? tab('') : tab;

            if (step === 'settings') {
                if (tabName === 'jobPostDetails') {
                    setActiveStep('video');
                }
            } else if (step === 'video') {
                if (tabName === 'preQualifyingQuestions') {
                    setActiveStep('prequal');
                }
            } else if (step === 'prequal') {
                if (tabName === 'videoMessageDetails') {
                    setActiveStep('video');
                } else if (tabName === 'cca') {
                    setActiveStep('cca');
                } else if (tabName === 'preview') {
                    setActiveTab('jobPostDetails');
                }
            } else if (step === 'cca') {
                if (tabName === 'preQualifyingQuestions') {
                    setActiveStep('prequal');
                } else if (tabName === 'preview') {
                    setActiveTab('jobPostDetails');
                }
            }
        };
    };

    return (
        <div className='flex flex-col gap-6 py-4'>
            {/* Step 1: Job Post Settings */}
            <div className='flex flex-col gap-2'>
                {activeStep === 'settings' ? (
                    <div>
                        <div
                            className='flex items-center gap-3 px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-t-xl'>
                            <div
                                className='flex items-center justify-center w-6 h-6 rounded-full bg-indigo-600 text-white font-bold text-xs shadow-sm'>
                                1
                            </div>
                            <span className='font-bold text-slate-800 text-sm'>
                {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.tabs.settings)}
              </span>
                            <span
                                className='ml-auto text-xs font-semibold text-indigo-600 bg-indigo-100/50 px-2 py-0.5 rounded-full'>
                Editing
              </span>
                        </div>
                        <div
                            className='border-x border-b border-indigo-100 rounded-b-xl bg-white shadow-sm overflow-hidden'>
                            <JobPostSettings
                                jobTitleList={jobTitleList}
                                jobPostingData={jobPostingData}
                                setJobPostingData={setJobPostingData}
                                setActiveTab={makeStepSetActiveTab('settings')}
                                jobDescription={jobDescription}
                                setJobPostId={setJobPostId}
                            />
                        </div>
                    </div>
                ) : (
                    <div
                        onClick={() => setActiveStep('settings')}
                        className='flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100/80 border border-slate-200/70 rounded-xl cursor-pointer transition-all duration-200 group shadow-sm'
                    >
                        <div className='flex items-center gap-3 min-w-0'>
                            <div
                                className={`flex items-center justify-center w-6 h-6 rounded-full font-bold text-xs ${
                                    isSettingsCompleted
                                        ? 'bg-emerald-500 text-white'
                                        : 'bg-slate-200 text-slate-500'
                                }`}
                            >
                                {isSettingsCompleted ? '✓' : '1'}
                            </div>
                            <div className='flex flex-col min-w-0'>
                <span className='font-bold text-slate-800 text-sm group-hover:text-indigo-600 transition-colors'>
                  {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.tabs.settings)}
                </span>
                                <span className='text-xs text-slate-500 truncate mt-0.5'>
                  {jobPostingData.jobTitle
                      ? `${jobPostingData.jobTitle} • ${jobPostingData.location || 'TBD'} • ${
                          jobPostingData.workType || 'TBD'
                      } • Dept: ${jobPostingData.department || 'TBD'}`
                      : 'Configure dates, job title, location, working arrangement, etc.'}
                </span>
                            </div>
                        </div>
                        <button
                            className='text-xs font-semibold text-slate-400 group-hover:text-indigo-600 px-3 py-1.5 rounded-lg border border-slate-200 bg-white group-hover:border-indigo-200 transition-all shadow-sm'>
                            Edit
                        </button>
                    </div>
                )}
            </div>

            {/* Step 2: Video Message */}
            <div className='flex flex-col gap-2'>
                {activeStep === 'video' ? (
                    <div>
                        <div
                            className='flex items-center gap-3 px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-t-xl'>
                            <div
                                className='flex items-center justify-center w-6 h-6 rounded-full bg-indigo-600 text-white font-bold text-xs shadow-sm'>
                                2
                            </div>
                            <span className='font-bold text-slate-800 text-sm'>
                {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.heading)}
              </span>
                            <span
                                className='ml-auto text-xs font-semibold text-indigo-600 bg-indigo-100/50 px-2 py-0.5 rounded-full'>
                Editing
              </span>
                        </div>
                        <div
                            className='border-x border-b border-indigo-100 rounded-b-xl bg-white shadow-sm overflow-hidden'>
                            <VideoMessageDetails
                                setActiveTab={makeStepSetActiveTab('video')}
                                jobPostingData={jobPostingData}
                                setJobPostingData={setJobPostingData}
                            />
                        </div>
                    </div>
                ) : (
                    <div
                        onClick={() => setActiveStep('video')}
                        className='flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100/80 border border-slate-200/70 rounded-xl cursor-pointer transition-all duration-200 group shadow-sm'
                    >
                        <div className='flex items-center gap-3 min-w-0'>
                            <div
                                className={`flex items-center justify-center w-6 h-6 rounded-full font-bold text-xs ${
                                    isVideoCompleted
                                        ? 'bg-emerald-500 text-white'
                                        : 'bg-slate-200 text-slate-500'
                                }`}
                            >
                                {isVideoCompleted ? '✓' : '2'}
                            </div>
                            <div className='flex flex-col min-w-0'>
                <span className='font-bold text-slate-800 text-sm group-hover:text-indigo-600 transition-colors'>
                  {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.heading)}
                </span>
                                <span className='text-xs text-slate-500 truncate mt-0.5'>
                  {jobPostingData.enableVideo
                      ? `Video Message Enabled ${
                          jobPostingData.videoTitle ? `• "${jobPostingData.videoTitle}"` : ''
                      }`
                      : 'Video message greeting is disabled for this job post.'}
                </span>
                            </div>
                        </div>
                        <button
                            className='text-xs font-semibold text-slate-400 group-hover:text-indigo-600 px-3 py-1.5 rounded-lg border border-slate-200 bg-white group-hover:border-indigo-200 transition-all shadow-sm'>
                            Edit
                        </button>
                    </div>
                )}
            </div>

            {/* Step 3: Pre-Qualifying Questions */}
            <div className='flex flex-col gap-2'>
                {activeStep === 'prequal' ? (
                    <div>
                        <div
                            className='flex items-center gap-3 px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-t-xl'>
                            <div
                                className='flex items-center justify-center w-6 h-6 rounded-full bg-indigo-600 text-white font-bold text-xs shadow-sm'>
                                3
                            </div>
                            <span className='font-bold text-slate-800 text-sm'>
                {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.heading)}
              </span>
                            <span
                                className='ml-auto text-xs font-semibold text-indigo-600 bg-indigo-100/50 px-2 py-0.5 rounded-full'>
                Editing
              </span>
                        </div>
                        <div
                            className='border-x border-b border-indigo-100 rounded-b-xl bg-white shadow-sm overflow-hidden'>
                            <PreQualifyingQuestions
                                setActiveTab={makeStepSetActiveTab('prequal')}
                                assessmentInfo={assessmentInfo}
                                setAssessmentInfo={setAssessmentInfo}
                                hasCCALicense={hasCCALicense}
                            />
                        </div>
                    </div>
                ) : (
                    <div
                        onClick={() => setActiveStep('prequal')}
                        className='flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100/80 border border-slate-200/70 rounded-xl cursor-pointer transition-all duration-200 group shadow-sm'
                    >
                        <div className='flex items-center gap-3 min-w-0'>
                            <div
                                className={`flex items-center justify-center w-6 h-6 rounded-full font-bold text-xs ${
                                    isPrequalCompleted
                                        ? 'bg-emerald-500 text-white'
                                        : 'bg-slate-200 text-slate-500'
                                }`}
                            >
                                {isPrequalCompleted ? '✓' : '3'}
                            </div>
                            <div className='flex flex-col min-w-0'>
                <span className='font-bold text-slate-800 text-sm group-hover:text-indigo-600 transition-colors'>
                  {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.heading)}
                </span>
                                <span className='text-xs text-slate-500 truncate mt-0.5'>
                  {preQualQuestionsCount > 0
                      ? `${preQualQuestionsCount} screening question(s) configured.`
                      : 'Define qualifying/screening questions for applicants.'}
                </span>
                            </div>
                        </div>
                        <button
                            className='text-xs font-semibold text-slate-400 group-hover:text-indigo-600 px-3 py-1.5 rounded-lg border border-slate-200 bg-white group-hover:border-indigo-200 transition-all shadow-sm'>
                            Edit
                        </button>
                    </div>
                )}
            </div>

            {/* Step 4: CCA Questions (Only if hasCCALicense is active) */}
            {hasCCALicense && (
                <div className='flex flex-col gap-2'>
                    {activeStep === 'cca' ? (
                        <div>
                            <div
                                className='flex items-center gap-3 px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-t-xl'>
                                <div
                                    className='flex items-center justify-center w-6 h-6 rounded-full bg-indigo-600 text-white font-bold text-xs shadow-sm'>
                                    4
                                </div>
                                <span className='font-bold text-slate-800 text-sm'>
                  Candidate Competency Assessment (CCA)
                </span>
                                <span
                                    className='ml-auto text-xs font-semibold text-indigo-600 bg-indigo-100/50 px-2 py-0.5 rounded-full'>
                  Editing
                </span>
                            </div>
                            <div
                                className='border-x border-b border-indigo-100 rounded-b-xl bg-white shadow-sm overflow-hidden p-4'>
                                <CCATab setActiveTab={makeStepSetActiveTab('cca')}/>
                            </div>
                        </div>
                    ) : (
                        <div
                            onClick={() => setActiveStep('cca')}
                            className='flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100/80 border border-slate-200/70 rounded-xl cursor-pointer transition-all duration-200 group shadow-sm'
                        >
                            <div className='flex items-center gap-3 min-w-0'>
                                <div
                                    className={`flex items-center justify-center w-6 h-6 rounded-full font-bold text-xs ${
                                        isCcaCompleted
                                            ? 'bg-emerald-500 text-white'
                                            : 'bg-slate-200 text-slate-500'
                                    }`}
                                >
                                    {isCcaCompleted ? '✓' : '4'}
                                </div>
                                <div className='flex flex-col min-w-0'>
                  <span className='font-bold text-slate-800 text-sm group-hover:text-indigo-600 transition-colors'>
                    Candidate Competency Assessment (CCA)
                  </span>
                                    <span className='text-xs text-slate-500 truncate mt-0.5'>
                    {ccaEnabled
                        ? `CCA Enabled • ${ccaQuestionsCount} assessment questions selected.`
                        : 'CCA is disabled for this job post.'}
                  </span>
                                </div>
                            </div>
                            <button
                                className='text-xs font-semibold text-slate-400 group-hover:text-indigo-600 px-3 py-1.5 rounded-lg border border-slate-200 bg-white group-hover:border-indigo-200 transition-all shadow-sm'>
                                Edit
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};